import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';

/**
 * Scroll behaviour for a single-page site whose nav is anchors.
 *
 * react-router does not restore or apply scroll position itself. Two cases:
 *   - a hash (/#plans, including arriving from /privacy) → scroll that section in
 *   - no hash → go to the top, because landing halfway down a fresh page after
 *     following a footer link reads as a broken page
 *
 * The rAF is not superstition: on a cold load of /#plans the element does not
 * exist yet when this effect first runs, so the lookup has to wait a frame for
 * the section to mount.
 */
const useScrollForLocation = (): void => {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (!hash) {
            window.scrollTo({ top: 0, behavior: 'auto' });
            return;
        }
        const id = decodeURIComponent(hash.slice(1));
        const frame = requestAnimationFrame(() => {
            document.getElementById(id)?.scrollIntoView({ block: 'start' });
        });
        return () => cancelAnimationFrame(frame);
    }, [pathname, hash]);
};

export const SiteLayout: React.FC = () => {
    const { t } = useTranslation();
    useScrollForLocation();

    return (
        <div className="site">
            <a className="skiplink" href="#main">{t('web.nav.skip')}</a>
            <SiteHeader />
            <main id="main" className="site__main">
                <Outlet />
            </main>
            <SiteFooter />
        </div>
    );
};
